import React, { useState, useEffect, useCallback, memo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import NotificationBadge from 'components/ui/NotificationBadge';
import UserStatusIndicator from 'components/ui/UserStatusIndicator';
import QuickActionPanel from 'components/ui/QuickActionPanel';
import NotificationCenter from 'components/NotificationCenter';

const NAV_SECTIONS = [
  {
    id: 'core',
    label: null,
    items: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        path: '/dashboard',
        icon: 'LayoutDashboard',
        tooltip: 'Command Center',
      },
    ],
  },
  {
    id: 'trading',
    label: 'Trading',
    items: [
      {
        id: 'stock-exchange',
        label: 'Stock Exchange',
        path: '/stock-exchange',
        icon: 'BarChart2',
        tooltip: 'Live market trading',
        badge: 0,
      },
      {
        id: 'stock-detail',
        label: 'Stock Detail',
        path: '/stock-detail',
        icon: 'TrendingUp',
        tooltip: 'Detailed stock analysis',
      },
      {
        id: 'portfolio',
        label: 'Portfolio',
        path: '/portfolio',
        icon: 'PieChart',
        tooltip: 'Your holdings',
        badge: 0,
      },
      {
        id: 'leaderboards',
        label: 'Leaderboards',
        path: '/leaderboards',
        icon: 'Trophy',
        tooltip: 'Global rankings',
      },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    items: [
      {
        id: 'businesses',
        label: 'Businesses',
        path: '/businesses',
        icon: 'Building2',
        tooltip: 'Manage your empire',
      },
      {
        id: 'crime-hub',
        label: 'Crime Hub',
        path: '/crime-hub',
        icon: 'Skull',
        tooltip: 'Underground operations',
        badge: 0,
      },
      {
        id: 'marketplace',
        label: 'Marketplace',
        path: '/marketplace',
        icon: 'ShoppingBag',
        tooltip: 'Buy and sell items',
      },
    ],
  },
  {
    id: 'social',
    label: 'Social',
    items: [
      {
        id: 'factions',
        label: 'Factions',
        path: '/factions',
        icon: 'Users',
        tooltip: 'Faction management',
        badge: 0,
      },
      {
        id: 'faction-chat',
        label: 'Faction Chat',
        path: '/faction-chat',
        icon: 'MessageSquare',
        tooltip: 'Faction communication',
        badge: 0,
      },
      {
        id: 'wars',
        label: 'Wars',
        path: '/wars',
        icon: 'Swords',
        tooltip: 'Territory wars',
        badge: 0,
        badgeType: 'error',
      },
    ],
  },
  {
    id: 'progression',
    label: 'Progression',
    items: [
      {
        id: 'quests-achievements',
        label: 'Quests',
        path: '/quests-achievements',
        icon: 'Trophy',
        tooltip: 'Quests & Achievements',
        badge: 0,
      },
      {
        id: 'seasonal-events',
        label: 'Seasonal Events',
        path: '/seasonal-events',
        icon: 'Calendar',
        tooltip: 'Seasonal events & competitions',
        badge: 0,
      },
      {
        id: 'season-pass',
        label: 'Season Pass',
        path: '/season-pass',
        icon: 'Star',
        tooltip: 'Season tiers & cosmetic rewards',
        badge: 0,
      },
    ],
  },
  {
    id: 'system',
    label: 'System',
    items: [
      {
        id: 'profile',
        label: 'Profile',
        path: '/profile',
        icon: 'UserCircle',
        tooltip: 'Your profile',
      },
      {
        id: 'notifications',
        label: 'Notifications',
        path: '/notifications',
        icon: 'Bell',
        tooltip: 'All notifications',
        badge: 0,
      },
    ],
  },
];

const ADMIN_SECTION = {
  id: 'admin',
  label: 'Admin',
  items: [
    {
      id: 'admin-panel',
      label: 'Admin Panel',
      path: '/admin',
      icon: 'Shield',
      tooltip: 'System administration',
    },
    {
      id: 'economy-dashboard',
      label: 'Economy',
      path: '/economy-dashboard',
      icon: 'Activity',
      tooltip: 'Economy overview',
    },
  ],
};

const JAIL_ITEM = {
  id: 'jail',
  label: 'Jail',
  path: '/jail',
  icon: 'Lock',
  tooltip: 'You are jailed',
  badgeType: 'error',
};

// Mobile bottom tab categories
const MOBILE_TABS = [
  { id: 'dashboard', label: 'Home', icon: 'LayoutDashboard', path: '/dashboard' },
  { id: 'trading', label: 'Trading', icon: 'BarChart2', path: '/stock-exchange' },
  { id: 'operations', label: 'Ops', icon: 'Building2', path: '/businesses' },
  { id: 'social', label: 'Social', icon: 'Users', path: '/factions' },
  { id: 'system', label: 'More', icon: 'Grid3X3', path: '/profile' },
];

// NavItem defined OUTSIDE MainSidebar to prevent recreation on every render
const NavItem = memo(({ item, isActive, isCollapsed, badgeCount, onNavClick }) => {
  const active = isActive(item?.path);

  return (
    <button
      onClick={() => onNavClick(item?.path)}
      className={`nav-item w-full text-left ${active ? 'active' : ''}`}
      title={isCollapsed ? item?.label : item?.tooltip}
      aria-current={active ? 'page' : undefined}
    >
      <Icon
        name={item?.icon}
        size={18}
        color={active ? 'var(--color-primary)' : 'currentColor'}
        strokeWidth={active ? 2.5 : 2}
      />
      {!isCollapsed && (
        <>
          <span className="flex-1 truncate">{item?.label}</span>
          {badgeCount > 0 && (
            <NotificationBadge
              count={badgeCount}
              type={item?.badgeType || 'default'}
              onClick={() => {}}
            />
          )}
        </>
      )}
      {isCollapsed && badgeCount > 0 && (
        <NotificationBadge
          count={badgeCount}
          type={item?.badgeType || 'default'}
          onClick={() => {}}
        />
      )}
    </button>
  );
});

const MainSidebar = ({
  isCollapsed = false,
  onToggleCollapse,
  userRole = 'player',
  jailStatus = false,
  jailTimer = 0,
  notificationCounts = {},
  userStats = {},
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close mobile sidebar on route change
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location?.pathname]);

  const isActive = useCallback((path) => {
    return location?.pathname === path || location?.pathname?.startsWith(path + '/');
  }, [location?.pathname]);

  const getBadgeCount = useCallback((itemId) => notificationCounts?.[itemId] || 0, [notificationCounts]);

  const handleNavClick = useCallback((path) => {
    navigate(path);
    if (isMobile) setIsMobileOpen(false);
  }, [navigate, isMobile]);

  const buildSections = useCallback(() => {
    const sections = [...NAV_SECTIONS];
    if (userRole === 'admin') sections?.push(ADMIN_SECTION);
    return sections;
  }, [userRole]);

  const SidebarContent = useCallback(() => (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Logo / Brand Header */}
      <div className="sidebar-header">
        <div className="sidebar-logo">
          <Icon name="Cpu" size={20} color="var(--color-primary)" />
        </div>
        {!isCollapsed && (
          <div className="sidebar-brand-text flex-1 min-w-0">
            <div className="font-heading text-sm font-bold text-primary truncate tracking-wider">
              NEXUS
            </div>
            <div className="font-caption text-xs text-muted-foreground truncate">
              Economic Terminal
            </div>
          </div>
        )}
        {/* Notifications (desktop, expanded) */}
        {!isCollapsed && !isMobile && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <NotificationCenter />
          </div>
        )}
        {/* Collapse toggle - desktop only */}
        {!isMobile && (
          <button
            onClick={onToggleCollapse}
            className="w-7 h-7 rounded flex items-center justify-center hover:bg-primary/10 transition-all duration-250 text-muted-foreground hover:text-primary flex-shrink-0"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Icon name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={14} />
          </button>
        )}
      </div>

      {/* Scrollable Nav Area */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden py-2 px-2 space-y-0.5">
        {/* Jail item - conditional */}
        {jailStatus && (
          <div className="mb-2">
            <NavItem
              item={JAIL_ITEM}
              isActive={isActive}
              isCollapsed={isCollapsed}
              badgeCount={0}
              onNavClick={handleNavClick}
            />
          </div>
        )}

        {buildSections()?.map((section) => (
          <div key={section?.id} className="mb-1">
            {section?.label && !isCollapsed && (
              <div className="nav-section-header">{section?.label}</div>
            )}
            {section?.label && isCollapsed && (
              <div className="my-2 mx-3 border-t border-border opacity-30" />
            )}
            <div className="space-y-0.5">
              {section?.items?.map((item) => (
                <NavItem
                  key={item?.id}
                  item={item}
                  isActive={isActive}
                  isCollapsed={isCollapsed}
                  badgeCount={getBadgeCount(item?.id)}
                  onNavClick={handleNavClick}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <QuickActionPanel isCollapsed={isCollapsed} />

      {/* User Status */}
      <UserStatusIndicator
        username={userStats?.username || 'Commander'}
        level={userStats?.level || 1}
        energy={userStats?.energy || 75}
        maxEnergy={userStats?.maxEnergy || 100}
        xp={userStats?.xp || 4200}
        xpToNext={userStats?.xpToNext || 5000}
        jailTimer={jailTimer}
        isCollapsed={isCollapsed}
        onProfileClick={() => handleNavClick('/profile')}
        onJailClick={() => handleNavClick('/jail')}
      />
    </div>
  ), [isCollapsed, isMobile, jailStatus, jailTimer, userStats, isActive, getBadgeCount, handleNavClick, buildSections, onToggleCollapse]);

  /* ── MOBILE LAYOUT ── */
  if (isMobile) {
    const totalBadges = Object.values(notificationCounts)?.reduce((a, b) => a + b, 0);

    return (
      <>
        {/* Mobile Menu Toggle Button */}
        <button
          onClick={() => setIsMobileOpen(true)}
          className="lg:hidden fixed top-4 left-4 z-50 w-10 h-10 rounded-md bg-card border border-border flex items-center justify-center shadow-glow-sm hover:bg-primary/10 transition-all duration-250"
          aria-label="Open navigation menu"
        >
          <Icon name="Menu" size={18} color="var(--color-primary)" />
        </button>
        {/* Mobile Slide-in Sidebar */}
        {isMobileOpen && (
          <>
            <div
              className="sidebar-overlay"
              onClick={() => setIsMobileOpen(false)}
              aria-hidden="true"
            />
            <aside
              className="fixed top-0 left-0 h-full z-navigation bg-secondary border-r border-border"
              style={{ width: '280px' }}
              role="navigation"
              aria-label="Main navigation"
            >
              <SidebarContent />
            </aside>
          </>
        )}
        {/* Bottom Tab Bar */}
        <nav className="mobile-tab-bar" role="navigation" aria-label="Mobile navigation">
          {MOBILE_TABS?.map((tab) => {
            const active = isActive(tab?.path);
            const tabBadge = tab?.id === 'system' ? totalBadges : getBadgeCount(tab?.id);
            return (
              <button
                key={tab?.id}
                onClick={() => navigate(tab?.path)}
                className={`mobile-tab-item ${active ? 'active' : ''}`}
                aria-current={active ? 'page' : undefined}
              >
                <div className="relative">
                  <Icon
                    name={tab?.icon}
                    size={20}
                    color={active ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
                    strokeWidth={active ? 2.5 : 2}
                  />
                  {tabBadge > 0 && (
                    <span
                      className="absolute -top-1.5 -right-1.5 min-w-[14px] h-[14px] px-1 rounded-full font-caption text-[9px] font-medium flex items-center justify-center"
                      style={{
                        background: 'var(--color-primary)',
                        color: 'var(--color-primary-foreground)',
                        fontSize: '9px',
                      }}
                    >
                      {tabBadge > 99 ? '99+' : tabBadge}
                    </span>
                  )}
                </div>
                <span>{tab?.label}</span>
              </button>
            );
          })}
        </nav>
      </>
    );
  }

  /* ── DESKTOP LAYOUT ── */
  return (
    <aside
      className={`fixed top-0 left-0 h-full bg-secondary border-r border-border z-navigation transition-all duration-250 ${
        isCollapsed ? 'sidebar-collapsed' : ''
      }`}
      style={{ width: isCollapsed ? '72px' : '280px' }}
      role="navigation"
      aria-label="Main navigation"
    >
      <SidebarContent />
    </aside>
  );
};

export default MainSidebar;